import { Component, Inject, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ElementWithdrawalService } from '../../../Services/element-withdrawal.service';
import { ElementReturnService } from '../../../Services/element-return.service';
import { WithdrawalLogModel } from '../../../Models/withdrawal-log.model';
import { ReturnLogModel } from '../../../Models/return-log.model';
import { forkJoin } from 'rxjs';



interface CombinedHistoryItem {
  id: number;
  type: 'withdrawal' | 'return';
  timestamp: string;
  userName: string;
  quantity: number;
  purpose?: string;
  reason?: string;
  comments?: string;
  isReturned?: boolean;
  returnTimestamp?: string;
  relatedWithdrawalId?: number;
}

@Component({
  selector: 'app-element-withdrawal-dialog',
  templateUrl: './element-withdrawal-dialog.component.html',
  styleUrls: ['./element-withdrawal-dialog.component.scss']
})
export class ElementWithdrawalDialogComponent implements OnInit {
  // Bestehende Properties
  withdrawalForm: FormGroup;

  // Neues Formular für Rückgaben
  returnForm: FormGroup;

  // Kombinierte Historie
  historyItems: CombinedHistoryItem[] = [];

  // Alte Historie-Properties
  withdrawalHistory: WithdrawalLogModel[] = [];
  returnHistory: ReturnLogModel[] = [];

  // Tab-Management
  activeTab: 'withdraw' | 'return' = 'withdraw';

  // Erweiterte Spalten für kombinierte Historie
  displayedColumns: string[] = ['timestamp', 'type', 'userName', 'quantity', 'purpose', 'comments', 'status'];

  isLoading = false;
  isSubmitting = false;

  // Map für schnelles Nachschlagen von Entnahmen
  private withdrawalMap = new Map<number, WithdrawalLogModel>();

  // Liste der kürzlich durchgeführten Entnahmen für mögliche Rückgaben
  recentWithdrawals: WithdrawalLogModel[] = [];

  constructor(
    private dialogRef: MatDialogRef<ElementWithdrawalDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { element: any },
    private fb: FormBuilder,
    private snackBar: MatSnackBar,
    private withdrawalService: ElementWithdrawalService,
    private returnService: ElementReturnService
  ) {
    // Entnahme-Formular
    this.withdrawalForm = this.fb.group({
      quantity: [1, [Validators.required, Validators.min(1), Validators.max(this.data.element.quantity)]],
      purpose: ['', Validators.required],
      comments: ['']
    });

    // Rückgabe-Formular
    this.returnForm = this.fb.group({
      quantity: [1, [Validators.required, Validators.min(1)]],
      returnType: ['newAddition', Validators.required],
      relatedWithdrawalId: [null],
      reason: ['', Validators.required],
      comments: ['']
    });
  }

  ngOnInit(): void {
    this.loadCombinedHistory();

    // Überwachen Sie Änderungen im returnType
    this.returnForm.get('returnType')?.valueChanges.subscribe(value => {
      const relatedWithdrawalId = this.returnForm.get('relatedWithdrawalId');

      if (value === 'returnedItem') {
        relatedWithdrawalId?.setValidators([Validators.required]);
        this.loadRecentWithdrawals();
      } else {
        relatedWithdrawalId?.clearValidators();
        relatedWithdrawalId?.setValue(null);
      }

      relatedWithdrawalId?.updateValueAndValidity();
    });

    // Überwachen Sie Änderungen in der ausgewählten Entnahme
    this.returnForm.get('relatedWithdrawalId')?.valueChanges.subscribe(value => {
      if (value) {
        const selectedWithdrawal = this.recentWithdrawals.find(w => w.id === value);
        if (selectedWithdrawal) {
          // Setze die Menge genau auf die entnommene Menge und deaktiviere das Feld
          this.returnForm.get('quantity')?.setValue(selectedWithdrawal.quantity);
          this.returnForm.get('quantity')?.disable();

          // Optional: Setze einen Hinweis als reason-Vorschlag
          this.returnForm.get('reason')?.setValue('Rückgabe der vollständigen Entnahme');
        }
      } else {
        // Wieder aktivieren und auf 1 zurücksetzen, wenn keine Entnahme ausgewählt ist
        this.returnForm.get('quantity')?.setValue(1);
        this.returnForm.get('quantity')?.enable();
        this.returnForm.get('quantity')?.setValidators([Validators.required, Validators.min(1)]);
        this.returnForm.get('quantity')?.updateValueAndValidity();

        // Zurücksetzen des Grundes
        this.returnForm.get('reason')?.setValue('');
      }
    });
  }

  // Lade sowohl Entnahmen als auch Rückgaben und kombiniere sie
  loadCombinedHistory(): void {
    this.isLoading = true;

    // Beide Historien parallel laden
    forkJoin({
      withdrawals: this.withdrawalService.getWithdrawalHistoryByElement(this.data.element.id),
      returns: this.returnService.getReturnHistoryByElement(this.data.element.id)
    }).subscribe({
      next: ({ withdrawals, returns }) => {
        // Speichere Originaldaten
        this.withdrawalHistory = withdrawals;
        this.returnHistory = returns;

        // Map erstellen für schnellen Lookup
        this.withdrawalMap.clear();
        withdrawals.forEach(w => this.withdrawalMap.set(w.id, w));

        // Markiere zurückgegebene Entnahmen
        withdrawals.forEach(w => {
          const relatedReturn = returns.find(r => r.relatedWithdrawalId === w.id);
          if (relatedReturn) {
            w.isReturned = true;
            w.returnTimestamp = relatedReturn.timestamp;
          } else {
            w.isReturned = false;
          }
        });

        // Kombiniere beide Arrays
        const withdrawalItems: CombinedHistoryItem[] = withdrawals.map(w => ({
          id: w.id,
          type: 'withdrawal' as const,
          timestamp: w.timestamp,
          userName: w.userName,
          quantity: w.quantity,
          purpose: w.purpose,
          comments: w.comments,
          isReturned: w.isReturned,
          returnTimestamp: w.returnTimestamp
        }));

        const returnItems: CombinedHistoryItem[] = returns.map(r => ({
          id: r.id,
          type: 'return' as const,
          timestamp: r.timestamp,
          userName: r.userName,
          quantity: r.quantity,
          reason: r.reason,
          comments: r.comments,
          relatedWithdrawalId: r.relatedWithdrawalId
        }));

        // Kombiniere und sortiere nach Zeitstempel (neueste zuerst)
        this.historyItems = [...withdrawalItems, ...returnItems].sort((a, b) =>
          new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
        );

        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error loading combined history:', error);
        this.snackBar.open('Fehler beim Laden der Historie', 'OK', { duration: 3000 });
        this.isLoading = false;
      }
    });
  }

  loadRecentWithdrawals(): void {
    this.withdrawalService.getWithdrawalHistoryByElement(this.data.element.id)
      .subscribe({
        next: (withdrawals) => {
          // Filtere nur aktive Entnahmen (die nicht bereits zurückgegeben wurden)
          this.recentWithdrawals = withdrawals.filter(w => w.isReturned === false);

          // Falls keine aktiven Entnahmen vorhanden sind und der Typ auf "returnedItem" steht,
          // automatisch auf "newAddition" umstellen
          if (this.recentWithdrawals.length === 0 &&
            this.returnForm.get('returnType')?.value === 'returnedItem') {
            this.returnForm.get('returnType')?.setValue('newAddition');
            this.snackBar.open('Es gibt keine aktiven Entnahmen für dieses Element. Wähle "Neue Hinzufügung".', 'OK',
              { duration: 5000 });
          }
        },
        error: (error) => {
          console.error('Error loading recent withdrawals:', error);
          this.snackBar.open('Fehler beim Laden der Entnahmehistorie', 'OK', { duration: 3000 });
        }
      });
  }

  // Tab wechseln
  setActiveTab(tab: 'withdraw' | 'return'): void {
    this.activeTab = tab;

    if (tab === 'return') {
      this.loadRecentWithdrawals();
    }
  }

  // Entnahme durchführen
  onSubmit(): void {
    if (this.withdrawalForm.invalid) {
      return;
    }

    const withdrawalData = {
      elementId: this.data.element.id,
      quantity: this.withdrawalForm.value.quantity,
      purpose: this.withdrawalForm.value.purpose,
      comments: this.withdrawalForm.value.comments
    };

    this.isSubmitting = true;
    this.withdrawalService.recordWithdrawal(withdrawalData)
      .subscribe({
        next: () => {
          this.snackBar.open('Entnahme erfolgreich registriert', 'OK', { duration: 3000 });
          this.loadCombinedHistory();
          this.withdrawalForm.reset({
            quantity: 1,
            purpose: '',
            comments: ''
          });
          // Update the element's quantity in the parent component
          this.data.element.quantity -= withdrawalData.quantity;
          this.isSubmitting = false;
        },
        error: (error) => {
          console.error('Error recording withdrawal:', error);
          this.snackBar.open('Fehler bei der Entnahme. Bitte versuchen Sie es erneut.', 'OK', { duration: 3000 });
          this.isSubmitting = false;
        }
      });
  }

  // Rückgabe durchführen
  onReturn(): void {
    if (this.returnForm.invalid) {
      return;
    }

    // Bei deaktiviertem Quantity-Feld den Wert direkt aus dem FormControl nehmen
    let quantity = this.returnForm.get('quantity')?.value;

    // Wenn das Feld deaktiviert ist (bei returnedItem), stellen wir sicher, dass der Wert korrekt verwendet wird
    if (this.returnForm.get('quantity')?.disabled) {
      // Bei Rückgabe mit Bezug auf eine bestimmte Entnahme
      if (this.returnForm.value.returnType === 'returnedItem' && this.returnForm.value.relatedWithdrawalId) {
        const selectedWithdrawal = this.recentWithdrawals.find(
          w => w.id === this.returnForm.value.relatedWithdrawalId
        );
        if (selectedWithdrawal) {
          quantity = selectedWithdrawal.quantity;
        }
      }
    }

    const returnData = {
      elementId: this.data.element.id,
      quantity: quantity, // Verwende den korrigierten Wert
      reason: this.returnForm.value.reason,
      comments: this.returnForm.value.comments,
      relatedWithdrawalId: this.returnForm.value.returnType === 'returnedItem' ?
        this.returnForm.value.relatedWithdrawalId : null
    };

    console.log('Return data:', returnData); // Debug-Ausgabe zur Überprüfung

    this.isSubmitting = true;
    this.returnService.recordReturn(returnData)
      .subscribe({
        next: () => {
          this.snackBar.open('Element erfolgreich hinzugefügt/zurückgegeben', 'OK', { duration: 3000 });
          this.loadCombinedHistory();
          this.returnForm.reset({
            quantity: 1,
            returnType: 'newAddition',
            relatedWithdrawalId: null,
            reason: '',
            comments: ''
          });
          // Nach dem Reset das Mengenfeld wieder aktivieren
          this.returnForm.get('quantity')?.enable();

          // Update the element's quantity in the parent component
          this.data.element.quantity += quantity; // Verwende den korrigierten Wert
          this.isSubmitting = false;

          // Lade die letzten Entnahmen neu, falls es Rückgaben gab
          if (returnData.relatedWithdrawalId) {
            this.loadRecentWithdrawals();
          }
        },
        error: (error) => {
          console.error('Error recording return:', error);
          this.snackBar.open('Fehler bei der Hinzufügung. Bitte versuchen Sie es erneut.', 'OK', { duration: 3000 });
          this.isSubmitting = false;
        }
      });
  }
  

  formatDate(dateString: string): string {
    return new Date(dateString).toLocaleString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  getRelatedWithdrawalDate(withdrawalId: number): string {
    const withdrawal = this.withdrawalMap.get(withdrawalId);
    if (withdrawal) {
      return this.formatDate(withdrawal.timestamp);
    }
    return 'Unbekannt';
  }

  onClose(): void {
    this.dialogRef.close(this.data.element);
  }
}
